/**
 * Quién desarrolla y opera esta plataforma.
 *
 * En un solo lugar porque el mismo dato aparece en el pie de cada página, en los Términos, en la
 * Política de Privacidad y en la página de la empresa. Con cuatro copias, la que se actualiza es
 * una y las otras tres quedan mintiendo — y en los Términos eso no es un detalle de estilo.
 *
 * Los datos son los mismos que figuran en las propuestas comerciales. **Tienen que coincidir**:
 * quien evalúa una propuesta entra al sitio a comprobar, y una diferencia entre los dos
 * documentos cuesta más credibilidad de la que suma cualquier adjetivo.
 */
import { PRESTADOR } from "@/lib/legal/prestador";

export const DEV_IA = {
    /** Nombre con el que se presenta el desarrollador. Es la capa de marca, no la legal. */
    nombre: "Dev IA",

    /**
     * Datos de contacto, leídos de `lib/legal/prestador.ts` para no tener dos copias.
     *
     * **Acá NO va la identificación personal.** El titular, el NIT y el NRC existen en
     * `prestador.ts` y alimentan los Términos de Servicio y la constancia en PDF, que es donde
     * un contrato los necesita y donde el departamento legal de un cliente los busca. En las
     * páginas comerciales la entidad es la marca: exponer los datos personales del titular no
     * aporta nada a la venta y los publica sin razón.
     */
    correo: PRESTADOR.correo,
    telefono: PRESTADOR.telefono,
    pais: PRESTADOR.domicilio,
} as const;

/**
 * Horario de atencion, en un solo lugar.
 *
 * Estaba escrito dos veces y **las dos copias se contradecian**: la pagina de Soporte decia
 * «Lunes a viernes» y el bloque de contacto del landing decia «Lunes a sabado». Un visitante
 * que mirara las dos paginas veia dos promesas distintas sobre cuando lo van a atender, y no
 * hay forma de que se entere quien las escribio.
 *
 * Mismo patron que dejo CAT-014 mal en cuatro copias despues de corregirse en la primera: al
 * cambiar un dato que se muestra, se busca donde mas aparece antes de darlo por cambiado.
 */
export const HORARIO_ATENCION = {
    dias: "Lunes a sábado",
    /**
     * La abreviatura, para los cuadros de estadisticas donde no entra la frase completa.
     *
     * Existe porque ya fallo una vez: el cuadro de /soporte decia «L–V» escrito a mano y siguio
     * diciendolo despues de que el horario pasara a sabado, contradiciendo al bloque de horario
     * que estaba quince lineas mas abajo en la misma pantalla.
     */
    diasCorto: "L–S",
    horas: "8:00 a.m. — 6:00 p.m.",
    /** El Salvador no tiene horario de verano: el desfase es fijo. */
    zona: "Hora El Salvador (UTC−6)",
    /** Una sola linea, para donde no entra en dos. */
    get linea() {
        return `${this.dias} · ${this.horas}`;
    },
} as const;

/**
 * El WhatsApp de atencion, en un solo lugar.
 *
 * Estaba escrito a mano en tres pantallas —/soporte, el boton de ayuda y el bloque de contacto—
 * y ademas habia un **cuarto** numero en `lib/payment-config.ts`: `50370000000`, un marcador de
 * posicion que nadie mostraba todavia. Publicarlo por accidente manda al cliente a un numero que
 * no existe, y el error no se ve desde el codigo que lo usa.
 *
 * El numero sale de `PRESTADOR.telefono` para que no haya dos verdades sobre el mismo dato.
 */
export const WHATSAPP = {
    /** Como se muestra en pantalla. */
    visible: PRESTADOR.telefono,
    /** Solo digitos: es el formato que exige `wa.me`. */
    numero: PRESTADOR.telefono.replace(/\D/g, ""),
    /** El enlace ya armado, con el texto del primer mensaje. */
    enlace(texto: string) {
        return `https://wa.me/${this.numero}?text=${encodeURIComponent(texto)}`;
    },
} as const;

/**
 * Lo que se promete sobre el tiempo de respuesta.
 *
 * Habia **tres promesas distintas** conviviendo: «menos de 2 horas» y «100% respuestas
 * garantizadas» en /soporte, y «menos de 24 horas» en el formulario del landing. Ninguna se
 * medía. Un cliente que lee dos de esas paginas ve dos compromisos diferentes, y el que queda
 * incumplido es siempre el mas corto.
 *
 * Se unifica en el mismo dia habil, que es lo que el horario de atencion permite sostener.
 */
export const RESPUESTA_SOPORTE = {
    corto: "Mismo día",
    frase: "el mismo día hábil",
} as const;

/** Lo que Dev IA desarrolla y mantiene. */
export const PRODUCTOS = [
    {
        nombre: "Factura Premium",
        descripcion: "Facturación electrónica: los once tipos de DTE del Ministerio de Hacienda.",
        url: "https://facturapremium.com",
    },
    {
        nombre: "ContaPremium",
        descripcion: "Contabilidad para El Salvador, integrada con la facturación.",
        /**
         * Sin enlace a propósito, hasta que tenga dominio propio.
         *
         * Hoy vive en un subdominio de Railway —enlazarlo desde una página comercial resta— y
         * `contapremium.com` pertenece a un sitio brasileño sin relación con este producto.
         * Nombrarlo con enlace mandaría al visitante a la marca equivocada.
         */
        url: null,
    },
] as const;
